'use client';

import { Box, Text, Counter, Flex, Button } from '@instamenu/ui-system-next';
import { formatCurrency } from '@instamenu/formatters';
import { observer } from 'mobx-react-lite';
import { imgStyle, menuItem } from './cart-menu.css';
import { EmptyImg } from '@instamenu/ui-icons';
import { Trash, Gift } from 'lucide-react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { palette, themeVars } from '@instamenu/ui-vanilla-extract';
import { OrderItem, useCheckoutContext } from '@instamenu/context-checkout';
import { makePath } from '../utils';

export type CardOrderItemProps = {
  orderItem: OrderItem;
};

export const CartOrderItem = observer((props: CardOrderItemProps) => {
  const router = useRouter();
  const checkout = useCheckoutContext();

  const onUpdateItemQuantity = (orderItem: OrderItem, quantity: number) =>
    orderItem?.update({ quantity });

  const { orderItem } = props;
  const { item, order } = orderItem;
  const isUnavailable = orderItem.isUnavailable;

  // Match order-item-list: only lock comped items when the program is dollar-based.
  // Item-based (punch-card) comped items remain editable so customers can tweak modifiers.
  const isLockedComped = orderItem.loyaltyComped && !!checkout?.loyalty?.isDollarBased;

  const handleItemNavigation = () => {
    if (isUnavailable || isLockedComped) return;
    router.push(
      makePath({
        restaurantId: order.restaurant.id,
        tableCode: order.table ? order.table : undefined,
        orderItemId: orderItem.id,
        menuId: checkout?.menuId,
      }),
    );
  };

  if (!checkout) return null;
  return (
    <Box data-id="cart-item" className={menuItem}>
      <Flex
        style={{
          gap: 10,
          cursor: isUnavailable || isLockedComped ? 'default' : 'pointer',
          pointerEvents: isUnavailable ? 'none' : 'auto',
          flex: 1,
        }}
        onClick={handleItemNavigation}
      >
        {item.imageUrl ? (
          <Image
            width={60}
            height={60}
            alt={item.imageUrl}
            className={imgStyle}
            src={item.imageUrl}
          />
        ) : (
          <EmptyImg width={60} height={60} className={imgStyle} />
        )}
        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Text maxLines={1} variant="caption1">
            {item.name}
          </Text>
          {isUnavailable && (
            <Text variant="caption2" css={{ color: palette.red600, fontWeight: 600 }}>
              Not available
            </Text>
          )}
          <Text maxLines={1} variant="caption2">
            {orderItem.modifiers.map((modifier) => modifier.name).join(', ')}
          </Text>
          <Text
            maxLines={1}
            wordBreak
            variant="caption2"
            css={{ color: palette.gray500, fontWeight: 400 }}
          >
            {orderItem.instructions}
          </Text>
          {!isUnavailable && (
            <Text variant="numeric">
              {orderItem.loyaltyComped ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {/* Strikethrough is the pre-comp *line* total so it matches
                      the live `price` (also a line total). Showing per-unit
                      `unitPrice` next to a line-total `price` is visually
                      mismatched for any qty > 1. */}
                  <span style={{ textDecoration: 'line-through' }}>
                    {formatCurrency(orderItem.unitPrice * orderItem.quantity)}
                  </span>
                  {formatCurrency(orderItem.price)}
                </span>
              ) : (
                formatCurrency(orderItem.price)
              )}
            </Text>
          )}
        </Box>
      </Flex>
      {isUnavailable ? (
        <Button variant="text" onClick={() => order.removeOrderItem(orderItem)}>
          <Trash size={18} color={palette.red600} />
        </Button>
      ) : orderItem.loyaltyComped ? (
        <Flex style={{ alignItems: 'center', gap: 8 }}>
          <Gift size={16} color={themeVars.color.brand} />
          <Button variant="text" onClick={() => order.removeOrderItem(orderItem)}>
            <Trash size={18} color={palette.red600} />
          </Button>
        </Flex>
      ) : (
        <Counter
          value={orderItem.quantity}
          max={
            typeof item.count === 'number'
              ? item.count - checkout.numInCart(item.id) + orderItem.quantity
              : undefined
          }
          onChange={(v) => onUpdateItemQuantity?.(orderItem, v)}
        />
      )}
    </Box>
  );
});
